'use client';

/**
 * Generic learning-path block. Supports two layouts:
 *
 * - {@code 'grid'} (default) — colored tile grid, each card links to
 *   the path-detail page. Original ThemeMaker port.
 * - {@code 'banner'} — full-width horizontal banner cards matching the
 *   legacy v2 {@code MyLearningJourneys.js} layout. Colored gradient
 *   panel on the inline-start, cover image fading in from the
 *   inline-end, per-course progress strip at the bottom, status pill
 *   top-end. Supports paginated "show more" via {@code pageSize}.
 *
 * <p>Two data modes:
 * <ul>
 *   <li><b>Live</b> (default) — when no tiles are authored, the block
 *       pulls the learner's paths from
 *       {@code LearningSource.fetchJourneys({ source, ids })}.</li>
 *   <li><b>Authored</b> — an explicit {@code journeys} array wins over
 *       the live feed and renders as-is, no fetch.</li>
 * </ul>
 * With no adapter mounted and no authored tiles, the block falls back
 * to {@link buildDefaultJourneys} so previews still render.
 *
 * <p>The BTK theme overrides this block with its own bespoke version —
 * see {@link @akademi/theme-btk}.
 */
import { useState, useCallback, useEffect, type ReactNode, type CSSProperties } from 'react';
import NextImage from 'next/image';
import { useTranslation } from 'react-i18next';
import {
  PortalLink,
  useCurrentUser,
  useLearningSource,
  useBlockPart,
  type LearningJourney,
} from '@akademi/site-builder-runtime';
import { Card } from '@akademi/ui';
import { tx, asList } from '../_shared/coerce';
import { cardStyle } from '../_shared/styles';
import { SectionShell } from '../_shared/SectionShell';
import type { PaddingBox } from '../_shared/resolvePadding';
import { EmptyState } from '../_shared/EmptyState';
import { useAsyncResource } from '../_shared/useAsyncResource';

type Localized = string | Record<string, string>;

interface Journey {
  id: string;
  title: Localized;
  count?: number;
  gradient?: string;
  /** Cover image — when present, sits behind a dark gradient overlay so
   *  the title stays legible. */
  imageUrl?: string;
  href?: string;
}

export interface LearningJourneysProps {
  title?: Localized;
  /** Design token size key e.g. "xl", "2xl". Maps to var(--font-size-{key}). */
  titleFontSize?: string;
  /** Authored tiles. When non-empty, these win over the live feed. */
  journeys?: Journey[];
  /** Live feed selector — host slug ({@code assigned} | {@code all} |
   *  {@code custom}). Ignored when {@code journeys} is authored. */
  source?: string;
  /** Path ids to show when {@code source === 'custom'}. */
  ids?: string[];
  /** Max tiles to fetch in live mode. */
  limit?: number;
  /** Label appended to the count in grid layout ("courses" → "8 courses"). */
  countLabel?: Localized;
  /**
   * Card layout style.
   * - {@code 'grid'} (default) — square colored tiles in a responsive grid.
   * - {@code 'banner'} — full-width horizontal banner cards (v2 style):
   *   colored panel left, image fades from right, progress strip bottom.
   */
  layout?: 'grid' | 'banner';
  /**
   * Number of cards (banner or grid tiles) shown initially. Each
   * "show more" click reveals the same number of additional cards.
   * Mirrors the legacy {@code PAGE_SIZE = 3}.
   */
  pageSize?: number;
  /** Label for the pagination button. */
  showMoreLabel?: Localized;
  /**
   * What the pagination button does once there are more cards than
   * {@code pageSize}.
   * - {@code 'loadMore'} (default) — reveals more cards in place.
   * - {@code 'seeAll'} — links out to {@code seeAllHref} instead.
   */
  moreMode?: 'loadMore' | 'seeAll';
  /** Link target for {@code moreMode === 'seeAll'}. */
  seeAllHref?: string;
  /**
   * Fallback gradient-panel background colour for banner cards when the
   * backend omits {@code journey.backgroundColor}. Mirrors legacy
   * {@code defaultBgColor = "#064e80"}.
   */
  defaultBgColor?: string;
  /**
   * Fallback title text colour for banner cards when the backend omits
   * a colour at index 2 of {@code journey.colors}. Mirrors legacy
   * {@code defaultColors[2] = "#FFFFFF"}.
   */
  defaultTextColor?: string;
  /** Number of columns in grid layout. Defaults to auto-fill. */
  columns?: number;
  /**
   * Auth-gate the whole block. {@code 'any'} (default) always renders;
   * {@code 'in'} only for signed-in users; {@code 'out'} only for
   * anonymous visitors.
   */
  requireAuth?: 'any' | 'in' | 'out';
  /** Sign-in CTA shown instead of the empty state when the live feed
   *  came back empty because the visitor isn't authenticated. */
  signedOutTitle?: Localized;
  signedOutBody?: Localized;
  signInLabel?: Localized;
  signInHref?: string;
  style?: CSSProperties;
  paddingOuter?: PaddingBox;
  paddingInner?: PaddingBox;
}

/** The i18next translate function, as much of it as this module uses. */
type Translate = (key: string) => string;

/**
 * Preview tiles drawn when nothing is authored and no learning-path
 * adapter is mounted. Built per call (not a module constant) so the copy
 * is read from the catalogue at render time and follows a language switch.
 */
function buildDefaultJourneys(t: Translate): Journey[] {
  return [
    {
      id: 'frontend',
      title: t('learningJourneys.demo.frontend'),
      count: 6,
      gradient: 'var(--color-brand-500, #3b5bdb)',
    },
    {
      id: 'leadership',
      title: t('learningJourneys.demo.leadership'),
      count: 4,
      gradient: 'var(--color-brand-600, #2a45b8)',
    },
    {
      id: 'data',
      title: t('learningJourneys.demo.data'),
      count: 8,
      gradient: 'var(--color-brand-800, #1a2d80)',
    },
  ];
}

/** Rotating tile background palette for live journeys (which carry no
 *  authored color). Keyed by index so adjacent tiles differ. */
const GRADIENT_PALETTE: string[] = [
  'var(--color-brand-500, #3b5bdb)',
  'var(--color-brand-600, #2a45b8)',
  'var(--color-brand-800, #1a2d80)',
  'var(--color-brand-700, #213aa0)',
];

function journeyToTile(j: LearningJourney, i: number): Journey {
  return {
    id: j.id,
    title: j.title,
    count: j.count,
    href: j.href,
    // Prefer thumbnail for list/banner views; fall back to full image.
    imageUrl: j.thumbnailUrl ?? j.imageUrl,
    gradient: GRADIENT_PALETTE[i % GRADIENT_PALETTE.length],
  };
}

export function LearningJourneys({
  title: authoredTitle,
  titleFontSize,
  journeys,
  source = 'assigned',
  ids,
  limit,
  countLabel: authoredCountLabel,
  layout = 'grid',
  pageSize = 3,
  showMoreLabel: authoredShowMoreLabel,
  moreMode = 'loadMore',
  seeAllHref = '/lxp/learning-journey',
  defaultBgColor = 'var(--color-brand-500, #064e80)',
  defaultTextColor = 'var(--color-text-on-brand, #ffffff)',
  columns,
  requireAuth,
  signedOutTitle,
  signedOutBody,
  signInLabel,
  signInHref = '/sign-in',
  style,
  paddingOuter,
  paddingInner,
}: LearningJourneysProps) {
  const { t } = useTranslation('common');
  const { t: tb } = useTranslation('site-builder-blocks');
  const title = authoredTitle ?? tb('learningJourneys.title');
  const countLabel = authoredCountLabel ?? tb('learningJourneys.countLabel');
  const showMoreLabel = authoredShowMoreLabel ?? tb('learningJourneys.showMore');
  const resolvedPageSize = pageSize ?? 3;
  const [visibleCount, setVisibleCount] = useState(resolvedPageSize);

  // Sync when the prop changes (e.g. admin edits the block config).
  useEffect(() => {
    setVisibleCount(resolvedPageSize);
  }, [resolvedPageSize]);

  const handleShowMore = useCallback(
    () => setVisibleCount((n) => n + resolvedPageSize),
    [resolvedPageSize],
  );

  const authored = asList<Journey>(journeys ?? []);
  const hasAuthored = authored.length > 0;

  const learning = useLearningSource();
  const currentUser = useCurrentUser();

  // Auth gate — mirror the shell's requireAuth semantics.
  const isSignedIn = currentUser != null;
  const gate = requireAuth ?? 'any';
  const hidden = (gate === 'in' && !isSignedIn) || (gate === 'out' && isSignedIn);

  // Live only when nothing is authored AND the host adapter exposes a
  // learning-path feed. Otherwise we render authored tiles / defaults
  // and skip the fetch entirely. Also skip when auth-gated out.
  const canFetch = !hidden && !hasAuthored && typeof learning?.fetchJourneys === 'function';
  const idsKey = ids ? ids.join(',') : '';

  const state = useAsyncResource<LearningJourney[]>(
    async (signal) => {
      if (!canFetch) return null;
      return await learning!.fetchJourneys!({ source, ids, limit, signal });
    },
    [canFetch, learning, source, idsKey, limit],
  );

  if (hidden) return null;

  // Resolve the full tile list per data mode.
  let list: Journey[];
  if (hasAuthored) {
    list = authored;
  } else if (!canFetch) {
    list = buildDefaultJourneys(tb);
  } else if (state.kind === 'loading') {
    const skeletonCount = resolvedPageSize;
    return (
      <SectionShell
        style={style}
        title={tx(title)}
        titleFontSize={titleFontSize}
        paddingOuter={paddingOuter}
        paddingInner={paddingInner}
      >
        {layout === 'banner' ? (
          <BannerList>
            {Array.from({ length: skeletonCount }, (_, i) => (
              <SkeletonBanner key={i} />
            ))}
          </BannerList>
        ) : (
          <TileGrid columns={columns}>
            {Array.from({ length: skeletonCount }, (_, i) => (
              <SkeletonTile key={i} />
            ))}
          </TileGrid>
        )}
      </SectionShell>
    );
  } else if (state.kind === 'loaded' && state.data.length > 0) {
    list = state.data.map(journeyToTile);
  } else {
    list = [];
  }

  if (list.length === 0) {
    // The feed came back empty because the visitor isn't authenticated
    // (the backend's learning-path search is employee-only) — a config
    // empty state would be misleading here, so point them at sign-in
    // instead. Authored tiles bypass the live feed entirely, so this
    // only applies to the live-fetch path.
    if (!hasAuthored && canFetch && !isSignedIn) {
      return (
        <SectionShell
          style={style}
          title={tx(title)}
          titleFontSize={titleFontSize}
          paddingOuter={paddingOuter}
          paddingInner={paddingInner}
        >
          <SignedOutCta
            title={tx(signedOutTitle) || t('learningJourneys.signedOutTitle')}
            body={tx(signedOutBody) || t('learningJourneys.signedOutBody')}
            label={tx(signInLabel) || t('learningJourneys.signInLabel')}
            href={signInHref}
          />
        </SectionShell>
      );
    }

    return (
      <SectionShell
        style={style}
        title={tx(title)}
        titleFontSize={titleFontSize}
        paddingOuter={paddingOuter}
        paddingInner={paddingInner}
      >
        <EmptyState
          title={t('learningJourneys.emptyTitle')}
          hint={t('learningJourneys.emptyHint')}
        />
      </SectionShell>
    );
  }

  // Banner layout — paginated "show more", horizontal cards.
  if (layout === 'banner') {
    const liveByIndex: LearningJourney[] =
      !hasAuthored && canFetch && state.kind === 'loaded' ? state.data : [];

    const displayed = list.slice(0, visibleCount);
    const hasMore = visibleCount < list.length;
    const seeAllInHeader = moreMode === 'seeAll' && hasMore;

    return (
      <SectionShell
        style={style}
        title={tx(title)}
        titleFontSize={titleFontSize}
        paddingOuter={paddingOuter}
        paddingInner={paddingInner}
        action={
          seeAllInHeader ? <SeeAllLink href={seeAllHref} label={tx(showMoreLabel)} /> : undefined
        }
      >
        <BannerList>
          {displayed.map((j, i) => (
            <BannerCard
              key={j.id}
              journey={j}
              liveJourney={liveByIndex[i]}
              defaultBgColor={defaultBgColor}
              defaultTextColor={defaultTextColor}
            />
          ))}
        </BannerList>
        {hasMore && moreMode === 'loadMore' && (
          <MorePaginationButton
            mode={moreMode}
            seeAllHref={seeAllHref}
            onLoadMore={handleShowMore}
            label={tx(showMoreLabel)}
          />
        )}
      </SectionShell>
    );
  }

  // Grid layout (default). Paginated the same way banner layout is —
  // `visibleCount` starts at `pageSize` and grows by `pageSize` on
  // each "show more" click.
  const gridDisplayed = list.slice(0, visibleCount);
  const gridHasMore = visibleCount < list.length;
  const gridSeeAllInHeader = moreMode === 'seeAll' && gridHasMore;

  return (
    <SectionShell
      style={style}
      title={tx(title)}
      titleFontSize={titleFontSize}
      paddingOuter={paddingOuter}
      paddingInner={paddingInner}
      action={
        gridSeeAllInHeader ? <SeeAllLink href={seeAllHref} label={tx(showMoreLabel)} /> : undefined
      }
    >
      <TileGrid columns={columns}>
        {gridDisplayed.map((j) => (
          <Card asChild key={j.id}>
            <a
              href={j.href ?? `/lxp/learning-journey/${j.id}`}
              style={{
                ...cardStyle,
                padding: 'clamp(18px, 3vw, 24px)',
                position: 'relative',
                overflow: 'hidden',
                background: j.imageUrl ? undefined : (j.gradient ?? GRADIENT_PALETTE[0]),
                color: 'var(--color-text-primary-inverse, #ffffff)',
                minHeight: 140,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                textDecoration: 'none',
                transition: 'transform 200ms ease, box-shadow 200ms ease',
              }}
            >
              {j.imageUrl && (
                <>
                  <NextImage
                    src={j.imageUrl}
                    alt=""
                    fill
                    sizes="(max-width: 640px) 100vw, 340px"
                    style={{ objectFit: 'cover' }}
                  />
                  <div
                    aria-hidden
                    style={{
                      position: 'absolute',
                      inset: 0,
                      background: 'linear-gradient(180deg, rgba(0,0,0,0.25), rgba(0,0,0,0.65))',
                    }}
                  />
                </>
              )}
              <div
                style={{
                  position: 'relative',
                  fontSize: 'clamp(15px, 1.8vw, 18px)',
                  fontWeight: 600,
                  lineHeight: 1.3,
                }}
              >
                {tx(j.title)}
              </div>
              {typeof j.count === 'number' && (
                <div
                  style={{
                    position: 'relative',
                    fontSize: 'clamp(12px, 1.5vw, 14px)',
                    opacity: 0.85,
                    marginTop: 8,
                  }}
                >
                  {j.count} {tx(countLabel)}
                </div>
              )}
            </a>
          </Card>
        ))}
      </TileGrid>
      {gridHasMore && moreMode === 'loadMore' && (
        <MorePaginationButton
          mode={moreMode}
          seeAllHref={seeAllHref}
          onLoadMore={handleShowMore}
          label={tx(showMoreLabel)}
        />
      )}
    </SectionShell>
  );
}

/** CTA shown in place of the empty state when the live feed is empty
 *  only because the visitor is signed out. */
function SignedOutCta({
  title,
  body,
  label,
  href,
}: {
  title: string;
  body: string;
  label: string;
  href: string;
}) {
  return (
    <Card
      style={{
        ...cardStyle,
        padding: 'clamp(20px, 4vw, 32px)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: 12,
        background:
          'linear-gradient(135deg, var(--color-brand-50, #eff6ff), var(--color-surface-card, #fff))',
      }}
    >
      <h3 style={{ margin: 0, fontSize: 'clamp(18px, 2.2vw, 24px)', fontWeight: 700 }}>{title}</h3>
      <p style={{ margin: 0, color: 'var(--color-text-tertiary, #6a6f73)' }}>{body}</p>
      <PortalLink
        href={href}
        style={{
          padding: 'var(--space-2, 8px) var(--space-4, 16px)',
          marginTop: 4,
          background: 'var(--color-brand-500, #18181b)',
          color: 'var(--color-text-on-brand, #fff)',
          borderRadius: 'var(--radius-md, 8px)',
          fontSize: 'var(--font-size-sm, 14px)',
          textDecoration: 'none',
          fontWeight: 500,
        }}
      >
        {label}
      </PortalLink>
    </Card>
  );
}

function TileGrid({ children, columns }: { children: ReactNode; columns?: number }) {
  const part = useBlockPart();
  if (!columns) {
    return (
      <div
        {...part('list')}
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(min(240px, 100%), 1fr))',
          gap: 'clamp(12px, 2vw, 16px)',
        }}
      >
        {children}
      </div>
    );
  }

  const n = Math.max(1, columns);
  return (
    <>
      {/* Fixed column count (exact `columns`), scaling down on
       *  narrower viewports so tiles don't get crushed. */}
      <style>{`
        .lj-tile-grid-${n} {
          display: grid;
          grid-template-columns: repeat(${n}, minmax(0, 1fr));
          gap: clamp(12px, 2vw, 16px);
        }
        @media (max-width: 900px) {
          .lj-tile-grid-${n} { grid-template-columns: repeat(${Math.min(n, 2)}, minmax(0, 1fr)); }
        }
        @media (max-width: 520px) {
          .lj-tile-grid-${n} { grid-template-columns: 1fr; }
        }
      `}</style>
      <div className={`lj-tile-grid-${n}`} {...part('list')}>
        {children}
      </div>
    </>
  );
}

function BannerList({ children }: { children: ReactNode }) {
  const part = useBlockPart();
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }} {...part('list')}>
      {children}
    </div>
  );
}

/**
 * Horizontal banner card — mirrors v2 JourneyCard.js:
 * coloured gradient panel on the inline-start, cover image masked from
 * the inline-end, status pill top-end, per-course progress strip bottom.
 */
function BannerCard({
  journey,
  liveJourney,
  defaultBgColor,
  defaultTextColor,
}: {
  journey: Journey;
  liveJourney?: LearningJourney;
  defaultBgColor: string;
  defaultTextColor: string;
}) {
  const { t } = useTranslation('site-builder-blocks');
  const bgColor = liveJourney?.backgroundColor ?? defaultBgColor;
  const colorParts = (liveJourney?.colors ?? '')
    .split(';')
    .map((s) => s.trim())
    .filter(Boolean);
  const textColor = colorParts[2] ?? defaultTextColor;
  const cover = liveJourney?.imageUrl ?? liveJourney?.thumbnailUrl ?? journey.imageUrl;
  const courses = liveJourney?.courses ?? [];
  const status = liveJourney?.attendeeStatus;

  const statusLabel =
    status === 'COMPLETED'
      ? t('learningJourneys.banner.completed')
      : status === 'IN_PROGRESS' || status === 'CONTINUING' || status === 'ATTENDING'
        ? t('learningJourneys.banner.inProgress')
        : null;

  return (
    <a
      href={journey.href ?? liveJourney?.href ?? `/lxp/learning-journey/${journey.id}`}
      title={tx(journey.title)}
      style={{
        position: 'relative',
        display: 'block',
        blockSize: 150,
        overflow: 'hidden',
        borderRadius: 'var(--radius-lg, 14px)',
        boxShadow: 'var(--shadow-sm, 0 1px 2px rgba(0,0,0,0.06))',
        textDecoration: 'none',
        background: `linear-gradient(90deg, ${bgColor} 0%, ${bgColor} 42%, var(--color-surface-raised, #ffffff) 88%)`,
        transition: 'transform 180ms ease, box-shadow 180ms ease',
      }}
    >
      {/* Cover — inline-end half, masked into the coloured panel. */}
      {cover && (
        <div
          aria-hidden
          style={{
            position: 'absolute',
            insetBlockStart: 0,
            insetBlockEnd: 0,
            insetInlineEnd: 0,
            inlineSize: '58%',
            WebkitMaskImage: 'linear-gradient(to left, #000 52%, transparent)',
            maskImage: 'linear-gradient(to left, #000 52%, transparent)',
          }}
        >
          <NextImage src={cover} alt="" fill sizes="58vw" style={{ objectFit: 'cover' }} />
        </div>
      )}

      {/* Status pill — top inline-end. */}
      {statusLabel && (
        <span
          style={{
            position: 'absolute',
            insetBlockStart: 10,
            insetInlineEnd: 10,
            zIndex: 2,
            padding: '2px 10px',
            borderRadius: 999,
            fontSize: 11,
            fontWeight: 700,
            background: status === 'COMPLETED' ? 'rgba(16,185,129,0.95)' : 'rgba(255,255,255,0.92)',
            color: status === 'COMPLETED' ? '#ffffff' : bgColor,
          }}
        >
          {statusLabel}
        </span>
      )}

      {/* Title + count — coloured inline-start panel. */}
      <div style={{ position: 'relative', zIndex: 1, inlineSize: '62%', padding: '18px 18px 0' }}>
        <h3
          style={{
            margin: 0,
            fontSize: 19,
            lineHeight: 1.3,
            fontWeight: 700,
            color: textColor,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {tx(journey.title)}
        </h3>
        {typeof journey.count === 'number' && (
          <span
            style={{
              display: 'inline-block',
              marginBlockStart: 8,
              fontSize: 12.5,
              fontWeight: 600,
              color: textColor,
              opacity: 0.85,
            }}
          >
            {t('learningJourneys.banner.courseCount', { count: journey.count })}
          </span>
        )}
      </div>

      {/* Per-course progress strip — green = completed. */}
      {courses.length > 0 && (
        <div
          style={{
            position: 'absolute',
            insetBlockEnd: 12,
            insetInlineStart: 18,
            inlineSize: '58%',
            zIndex: 2,
            display: 'flex',
            gap: 4,
          }}
        >
          {courses.map((c, i) => {
            const done = (c.completionPercentage ?? 0) >= 100;
            return (
              <span
                key={i}
                style={{
                  flex: 1,
                  blockSize: 5,
                  borderRadius: 999,
                  background: done ? '#10b981' : 'color-mix(in srgb, #ffffff 55%, transparent)',
                }}
              />
            );
          })}
        </div>
      )}
    </a>
  );
}

function SkeletonTile() {
  return (
    <Card
      aria-hidden
      style={{
        ...cardStyle,
        minHeight: 140,
        background: 'var(--color-surface-muted, rgba(0,0,0,0.06))',
        animation: 'pulse 1.4s ease-in-out infinite',
      }}
    />
  );
}

function SkeletonBanner() {
  return (
    <div
      aria-hidden
      style={{
        blockSize: 150,
        borderRadius: 'var(--radius-lg, 14px)',
        background: 'var(--color-surface-muted, rgba(0,0,0,0.06))',
        animation: 'pulse 1.4s ease-in-out infinite',
      }}
    />
  );
}

/** Header-row "see all" link — title on the inline-start, this on the
 *  inline-end, {@code space-between} (via SectionShell's {@code action}
 *  slot), matching the rail-block header pattern used elsewhere. */
function SeeAllLink({ href, label }: { href: string; label: ReactNode }) {
  return (
    <a
      href={href}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        fontSize: 'var(--font-size-sm, 14px)',
        fontWeight: 600,
        // brand-500 is tuned for saturated fills, not small text — it read
        // ~3.2:1 against white here. brand-600 is the text-safe shade the
        // sibling _LearningRailFrame "see all" link already uses.
        color: 'var(--color-brand-600, #1c6a80)',
        textDecoration: 'none',
        flexShrink: 0,
      }}
    >
      {label}
      <span aria-hidden style={{ fontSize: '1.1em', lineHeight: 1 }}>
        →
      </span>
    </a>
  );
}

/** Pagination affordance shown once there are more cards than
 *  {@code pageSize}. 'loadMore' reveals more in place; 'seeAll'
 *  links out to {@code seeAllHref} instead. */
function MorePaginationButton({
  mode,
  seeAllHref,
  onLoadMore,
  label,
}: {
  mode: 'loadMore' | 'seeAll';
  seeAllHref: string;
  onLoadMore: () => void;
  label: ReactNode;
}) {
  const style: CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    padding: '8px 24px',
    border: '1.5px solid var(--color-border-subtle, rgba(0,0,0,0.15))',
    borderRadius: 'var(--radius-md, 8px)',
    background: 'var(--color-surface-card, #ffffff)',
    color: 'var(--color-text-primary, #18181b)',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
    textDecoration: 'none',
  };

  const part = useBlockPart();
  return (
    <div style={{ display: 'flex', justifyContent: 'center', marginTop: 24 }}>
      {mode === 'seeAll' ? (
        <a href={seeAllHref} style={style} {...part('pager')}>
          {label}
        </a>
      ) : (
        <button type="button" onClick={onLoadMore} style={style} {...part('pager')}>
          <ChevronDownGlyph />
          {label}
        </button>
      )}
    </div>
  );
}

function ChevronDownGlyph() {
  return (
    <svg aria-hidden="true" width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path
        d="M3 6l5 5 5-5"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
